import { rem } from 'polished'
import { useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import tripPlannerGlossaryMessages from 'tripPlanner/i18n/tripPlannerGlossaryMessages'
import styled from 'styled-components'

import PriceAndCtaOrSoldOut from './PriceAndCtaOrSoldOut'
import SimpleBookmarkButton from './SimpleBookmarkButton'
import WishlistGuard from 'wishlists/components/WishlistGuard'

import ClickableLink from 'components/Common/Clickable/ClickableLink'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getDistanceLabel } from 'lib/customer/recommendationUtils'
import type { DistanceUnit } from 'lib/geo/distanceUnits'
import { cardItemHoverAnimation } from 'tripPlanner/utils/css'

const Container = styled(ClickableLink)`
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${(props) => props.theme.borderRadius.M};

  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${cardItemHoverAnimation}
`

const Image = styled.div`
  height: ${rem(173)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${mediaQueryUp.tablet} {
    height: ${rem(186)};
  }

  background-size: cover;
  background-position: center;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  padding-block: ${rem(16)} ${0};
  padding-inline: ${rem(16)};
`

const StickToBottom = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-block: ${rem(8)} ${rem(16)};
  padding-inline: ${rem(16)};
  flex-grow: 1;
`

const TopBadges = styled.div`
  margin: ${rem(12)};
`

const BookmarkButtonBottomLayout = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-inline-end: ${rem(12)};

  & > * {
    transform: translateY(50%);
  }
`

export type BookmarkCardProps = {
  title?: string
  body?: string | React.ReactElement
  bodyLines?: number
  additionalContent?: React.ReactElement
  urgencyLabels?: Array<App.OfferUrgencyLabel>
  onClick?: () => void
  onBookmarkButtonClick?: () => void
  bookmarkButton: React.ReactElement | undefined
  isBookmarked?: boolean
  productType?: App.ProductType
  offerUrl: string | undefined
  imageUrl: string | undefined
  distance?: number | null
  distanceFrom?: string | null
  distanceUnit?: DistanceUnit
  location: React.ReactElement
  offerPriceDetails?: React.ReactElement
  isSoldOut?: boolean
  className?: string
  target?: '_blank'
}

const BookmarkCard = React.forwardRef<HTMLAnchorElement, BookmarkCardProps>(
  (
    {
      title,
      body,
      bodyLines,
      additionalContent,
      urgencyLabels,
      productType,
      offerUrl,
      offerPriceDetails,
      imageUrl,
      bookmarkButton,
      isBookmarked,
      onClick,
      onBookmarkButtonClick,
      distance,
      distanceFrom,
      distanceUnit,
      location,
      isSoldOut,
      className,
      target,
    },
    ref,
  ) => {
    const intl = useIntl()
    const savedChipLabel = intl.formatMessage({
      id: 'UPO_njLWC3BwbFH8Rvpo',
      defaultMessage: 'Saved',
    })
    const saveToTripChipLabel = intl.formatMessage(tripPlannerGlossaryMessages.saveToTrip)
    const soldOutLabel = intl.formatMessage({
      id: 'EFjmN_Abz9MoaUKe1_MC',
      defaultMessage: 'Sold out',
    })

    const distanceText = useMemo(() => {
      if (!distanceFrom || !distance || !distanceUnit) {
        return null
      }
      return getDistanceLabel(distance, distanceFrom, distanceUnit, intl)
    }, [distance, distanceUnit, distanceFrom, intl])

    return (
      <Container
        ref={ref}
        to={offerUrl}
        onClick={onClick}
        className={className}
        data-testid="bookmark-card"
        target={target}
      >
        {imageUrl && (
          <Image style={{ backgroundImage: `url(${imageUrl})` }}>
            <TopBadges>
              {productType && <ProductTypeLabel productType={productType} />}
            </TopBadges>
            <BookmarkButtonBottomLayout>
              {bookmarkButton || (
                <WishlistGuard>
                  <SimpleBookmarkButton
                    isSaved={isBookmarked ?? false}
                    onClick={onBookmarkButtonClick}
                    label={isBookmarked ? savedChipLabel : saveToTripChipLabel}
                  />
                </WishlistGuard>
              )}
            </BookmarkButtonBottomLayout>
          </Image>
        )}
        <Content>
          {!isSoldOut && <OfferLabels urgencyLabels={urgencyLabels} />}
          {isSoldOut && (
            <div>
              <UrgencyLabel variant="critical2">{soldOutLabel}</UrgencyLabel>
            </div>
          )}
          {distanceText && (
            <BodyText
              variant="small"
              lineClamp={1}
              wrap="no-wrap"
              colour="light-foreground-alt"
            >
              {distanceText}
            </BodyText>
          )}
          {location}
          <VerticalSpacer gap={4}>
            {title && (
              <Heading lineClamp={2} variant="heading6">
                {title}
              </Heading>
            )}
            {body && (
              <BodyText
                lineClamp={bodyLines ?? 2}
                variant="medium"
                weight="normal"
              >
                {body}
              </BodyText>
            )}
            {additionalContent}
          </VerticalSpacer>
        </Content>
        <StickToBottom>
          <PriceAndCtaOrSoldOut
            isSoldOut={isSoldOut ?? false}
            offerPriceDetails={offerPriceDetails}
          />
        </StickToBottom>
      </Container>
    )
  },
)

BookmarkCard.displayName = 'BookmarkCard'

export default BookmarkCard
